import React from "react";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useHarmonicIntervalFn, useUpdate } from "react-use";
import style from "./voting-countdown.module.css";
import { secondsLeft, useActiveVotingSession, checkParticipation, votingAtom, Participation } from "./voting-common";
import { useAtom } from "jotai";

/**================================================================================================
 **                                      VotingSessionCountdown
 *
 * This component's job is to render a countdown in the last 10 seconds of a voting session.
 * The countdown is shown at the bottom middle of the screen, and only if the user is participating
 * in the session, and hasn't marked himself as "done".
 *
 * To create a countdown in react, we have to cause the component to re-render every second.
 * We can do this by using the useHarmonicIntervalFn hook from react-use.
 * useHarmonicIntervalFn hook fires at the same time for all components using it (so they are synchronized)
 *================================================================================================**/
export function VotingSessionCountdown({ syncService }: { syncService: SyncService<RW> | null }) {
  const current = useActiveVotingSession(syncService);
  const [state] = useAtom(votingAtom);
  const participation = checkParticipation(state, current);
  const inSession = participation === Participation.Joined || participation === Participation.Done;
  const left = secondsLeft(current) || -1;
  let timeDelay = null;
  if (inSession) {
    timeDelay = left <= 0 ? null : left <= 10 ? 1000 : (left - 10) * 1000;
  }
  useHarmonicIntervalFn(useUpdate(), timeDelay);

  if (left > 0 && left <= 10 && inSession) {
    return (
      <div className={style.overlayBottomCenter}>
        <div className={style.countdown}>{left}</div>
      </div>
    );
  }
  return null;
}
